import {Box, Grid, Theme} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import React, {FC, useCallback, useMemo, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {useSelector} from 'react-redux';
import AB_Dim_420 from '../../../../assets/products/product-1/AB_Dim_420.jpg';
import {localized, localizedInterpolation} from '../../../../i18n/i18n';
import {AppState} from '../../../../state/store';
import {
  getUnitLenghtByCulture,
  getValueLengthWithUnitByCulture,
  validCountryIds,
} from '../../../../utilities/helpers/culture-settings-helper';
import {manuelNumberCheck} from '../../../../utilities/helpers/form-helper';
import {
  getConfigurationParamByName,
  getConfigurationParamByNameAndCulture,
} from '../../../../utilities/helpers/product-helper';
import {BinaryButtonInput} from '../../../components/custom-components/configuration/dynamic-components/binary-button-input';
import {ImageRow} from '../../../components/custom-components/configuration/dynamic-components/image-row';
import {PlacementWarningRow} from '../../../components/custom-components/configuration/dynamic-components/placement-warning-row';
import {ValueRangeInput} from '../../../components/custom-components/configuration/dynamic-components/value-range-input';
import {
  liftingUnitStrength,
  tableColor,
  tableTravel,
  tableUseCase,
  worktopDepth,
  worktopLength,
} from '../configuration-constants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    grid: {
      width: '686px',
      [theme.breakpoints.down('lg')]: {
        width: '500px',
      },
    },
    gridItem: {
      height: '140px',
    },
  }),
);

export interface PropsFromParent {}

export const Product1Step1: FC<PropsFromParent> = React.memo(({...props}) => {
  const classes = useStyles();
  const [showPlacementWarning, setShowPlacmentWarning] = useState<boolean>(false);
  const cultureUrlParameters = useSelector((store: AppState) => store.urlSettingsReducer.cultureUrlParameters);
  const selectedProduct = useSelector((store: AppState) => store.projectReducer.selectedProduct);
  const {unitsId} = useSelector((store: AppState) => store.urlSettingsReducer.cultureUrlParameters);
  const [maxTravel, setMaxTravel] = useState<string>(
    getConfigurationParamByName(selectedProduct, tableUseCase) === 'true' ? '300' : '600',
  );
  const formContext = useFormContext();

  const between400And3000 = useCallback(
    (event: any) => {
      manuelNumberCheck(
        event.target.name,
        event.target.value,
        getUnitLenghtByCulture(unitsId, '400'),
        getUnitLenghtByCulture(unitsId, '3000'),
        unitsId,
        formContext,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitsId],
  );

  const between550And700 = useCallback(
    (event: any) => {
      manuelNumberCheck(
        event.target.name,
        event.target.value,
        getUnitLenghtByCulture(unitsId, '550'),
        getUnitLenghtByCulture(unitsId, '700'),
        unitsId,
        formContext,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitsId],
  );

  const between200AndMaxTravel = useCallback(
    (event: any) => {
      manuelNumberCheck(
        event.target.name,
        event.target.value,
        getUnitLenghtByCulture(unitsId, '200'),
        getUnitLenghtByCulture(unitsId, maxTravel),
        unitsId,
        formContext,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitsId, maxTravel],
  );

  const useCaseBtnCallback = useCallback((value: boolean) => {
    setShowPlacmentWarning(!value);
    if (value) {
      setMaxTravel('600');
    } else {
      setMaxTravel('300');
    }
  }, []);

  const getTableTravelDefaultValue = useMemo(() => {
    const defaultValue = getConfigurationParamByNameAndCulture(selectedProduct, tableTravel, unitsId);
    if (defaultValue) {
      return defaultValue;
    } else {
      return getUnitLenghtByCulture(unitsId, '300');
    }
  }, [selectedProduct, unitsId]);

  return (
    <>
      <Box display="flex" justifyContent="center" alignItems="center" sx={{flexGrow: 1}}>
        <Grid container spacing={2} className={classes.grid}>
          <Grid container spacing={6}>
            <ImageRow imgString={AB_Dim_420} />
          </Grid>

          <Grid container spacing={6} className={classes.gridItem}>
            <ValueRangeInput
              formName={worktopLength}
              minValue={'400'}
              maxValue={'3000'}
              textInputLabel={localized('LengthOfTheTableTop') + ' (A)'}
              tooltipLabel={localizedInterpolation('ToolTipProduct1Step1_1', {
                value1: getValueLengthWithUnitByCulture(unitsId, '400'),
                value2: getValueLengthWithUnitByCulture(unitsId, '3000'),
              })}
              formOnChange={between400And3000}
            />
          </Grid>

          <Grid container spacing={6} className={classes.gridItem}>
            <ValueRangeInput
              formName={worktopDepth}
              minValue={'550'}
              maxValue={'700'}
              textInputLabel={localized('DepthOfTheTableTop') + ' (B)'}
              tooltipLabel={localizedInterpolation('ToolTipProduct1Step1_2', {
                value1: getValueLengthWithUnitByCulture(unitsId, '550'),
                value2: getValueLengthWithUnitByCulture(unitsId, '700'),
              })}
              formOnChange={between550And700}
            />
          </Grid>

          <Grid container spacing={6} className={classes.gridItem}>
            <BinaryButtonInput
              formName={liftingUnitStrength}
              value1={'100'}
              value2={'150'}
              valueUnit={'kg'}
              label={localized('LiftingUnitLiftStrengthIs')}
              tooltipLabel={localized('ToolTipProduct1Step1_3')}
            />
          </Grid>

          <Grid container spacing={6} className={classes.gridItem}>
            <BinaryButtonInput
              formName={tableUseCase}
              value1={'true'}
              value2={'false'}
              btnLabel1={localized('Yes')}
              btnLabel2={localized('No')}
              label={localized('TabletopForWasingOrCooking')}
              tooltipLabel={localizedInterpolation('ToolTipProduct1Step1_4', {
                value1: getValueLengthWithUnitByCulture(unitsId, '70'),
                value2: getValueLengthWithUnitByCulture(unitsId, '500'),
              })}
              buttonPressedCallback={useCaseBtnCallback}
            />
          </Grid>

          <PlacementWarningRow showPlacementWarning={showPlacementWarning} />

          <Grid container spacing={6} className={classes.gridItem}>
            <ValueRangeInput
              formName={tableTravel}
              minValue={'200'}
              maxValue={maxTravel}
              defaultValue={getTableTravelDefaultValue}
              textInputLabel={localized('WhatHeightAdjustmentIsDesired')}
              tooltipLabel={localizedInterpolation('ToolTipProduct1Step1_5', {
                value1: getValueLengthWithUnitByCulture(unitsId, '200'),
                value2: getValueLengthWithUnitByCulture(unitsId, '600'),
                value3: getValueLengthWithUnitByCulture(unitsId, '300'),
              })}
              formOnChange={between200AndMaxTravel}
            />
          </Grid>
          {cultureUrlParameters.contryId !== validCountryIds.us &&
            cultureUrlParameters.contryId !== validCountryIds.au && (
              <Grid container spacing={6} className={classes.gridItem}>
                <BinaryButtonInput
                  formName={tableColor}
                  value1={'White'}
                  value2={'Grey'}
                  btnLabel1={localized('White')}
                  btnLabel2={localized('Grey')}
                  label={localized('WhatLiftingUnitColor')}
                />
              </Grid>
            )}
        </Grid>
      </Box>
    </>
  );
});
